import React, { useCallback, useEffect, useRef, useState } from "react";
import { LayoutDashboard, Route as RouteIcon, History, User, Map, Car, IdCard, Camera, Plus, CheckCircle2, Star } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import { useAuth } from "@/context/AuthContext";
import { driverApi } from "@/lib/driverApi";
import { resolveUploadUrl, resolveDriverPhotoUrl } from "@/lib/api";

const navItems = [
  { to: "/conductor", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/conductor/ruta", label: "Ruta optimizada", icon: RouteIcon },
  { to: "/conductor/rutas-tarifas", label: "Mis rutas", icon: Map },
  { to: "/conductor/historial", label: "Historial", icon: History },
  { to: "/conductor/calificaciones", label: "Calificaciones", icon: Star },
  { to: "/conductor/perfil", label: "Perfil", icon: User },
];
const bottomNav = [
  { to: "/conductor", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/conductor/ruta", label: "Ruta", icon: RouteIcon },
  { to: "/conductor/rutas-tarifas", label: "Rutas", icon: Map },
  { to: "/conductor/historial", label: "Viajes", icon: History },
  { to: "/conductor/perfil", label: "Perfil", icon: User },
];

const CATEGORY_LABELS = { sedan: "Sedán", suv: "SUV", van: "Van" };

const EMPTY_VEHICLE = {
  brand: "",
  model: "",
  year: "",
  color: "",
  plate: "",
  passenger_capacity: "",
  luggage_capacity: "",
  category: "sedan",
};

const DriverProfile = () => {
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ dni: "", license_number: "", license_expires_at: "" });
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [vehicleForm, setVehicleForm] = useState(EMPTY_VEHICLE);
  const [savingVehicle, setSavingVehicle] = useState(false);

  const profilePhotoInput = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const { data } = await driverApi.getProfile();
      setProfile(data);
      setProfileForm({
        dni: data.dni,
        license_number: data.license_number,
        license_expires_at: data.license_expires_at,
      });
    } catch (err) {
      if (err.response?.status !== 404) setError("No se pudo cargar el perfil.");
      setProfile(null);
    }

    try {
      const { data } = await driverApi.listVehicles();
      setVehicles(data);
    } catch {
      setVehicles([]);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const submitProfile = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setSavingProfile(true);
    try {
      const { data } = await driverApi.upsertProfile(profileForm);
      setProfile(data);
      setNotice("Perfil guardado.");
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo guardar el perfil. Revisa los datos.");
    } finally {
      setSavingProfile(false);
    }
  };

  const uploadProfilePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    try {
      const { data } = await driverApi.uploadProfilePhoto(file);
      setProfile(data);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo subir la foto.");
    } finally {
      e.target.value = "";
    }
  };

  const submitVehicle = async (e) => {
    e.preventDefault();
    setError("");
    setSavingVehicle(true);
    try {
      await driverApi.createVehicle({
        ...vehicleForm,
        year: Number(vehicleForm.year),
        passenger_capacity: Number(vehicleForm.passenger_capacity),
        luggage_capacity: vehicleForm.luggage_capacity ? Number(vehicleForm.luggage_capacity) : null,
      });
      setShowVehicleForm(false);
      setVehicleForm(EMPTY_VEHICLE);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo crear el vehículo. Revisa los datos.");
    } finally {
      setSavingVehicle(false);
    }
  };

  const activateVehicle = async (id) => {
    setError("");
    try {
      await driverApi.activateVehicle(id);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo activar el vehículo.");
    }
  };

  const uploadVehiclePhoto = async (vehicleId, file) => {
    setError("");
    try {
      await driverApi.uploadVehiclePhoto(vehicleId, file);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo subir la foto del vehículo.");
    }
  };

  if (loading) {
    return (
      <PanelShell role="Conductor" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="driver-profile">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12 text-sm text-[#A0A0A5] font-body">Cargando…</div>
      </PanelShell>
    );
  }

  return (
    <PanelShell
      role="Conductor"
      navItems={navItems}
      bottomNavItems={bottomNav}
      userName={user?.full_name}
      userMeta={profile ? "Perfil completo" : "Perfil incompleto"}
      testId="driver-profile"
    >
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Mi cuenta</p>
        <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Perfil del conductor</h1>

        {error && (
          <p className="mt-4 text-xs text-red-400 font-body" data-testid="driver-profile-error">
            {error}
          </p>
        )}
        {notice && <p className="mt-4 text-xs text-[#C9A227] font-body">{notice}</p>}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
          {/* Profile card */}
          <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6 text-center" data-testid="driver-profile-card">
            <div className="relative w-24 h-24 mx-auto">
              {profile?.photo_url ? (
                <img
                  src={resolveDriverPhotoUrl(profile)}
                  alt="Foto de perfil"
                  className="w-24 h-24 rounded-full object-cover border-2 border-[#C9A227]"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#C9A227]/15 border-2 border-[#C9A227] flex items-center justify-center text-[#C9A227] font-display font-bold text-3xl">
                  {(user?.full_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2)}
                </div>
              )}
              <button
                type="button"
                onClick={() => profilePhotoInput.current?.click()}
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#C9A227] flex items-center justify-center text-[#0D0E10]"
                data-testid="upload-photo"
              >
                <Camera size={14} />
              </button>
              <input ref={profilePhotoInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadProfilePhoto} />
            </div>
            <h2 className="font-display font-bold text-xl mt-4">{user?.full_name}</h2>
            <p className="text-xs text-[#A0A0A5] mt-1">Conductor ejecutivo</p>

            <div className="hr-gold my-6" />

            <div className="text-left space-y-3 text-xs">
              <Row label="Teléfono" value={user?.phone || "—"} />
              <Row label="Correo" value={user?.email} />
              <Row label="DNI" value={profile?.dni || "—"} />
              <Row label="Licencia (Q)" value={profile?.license_number || "—"} />
              <Row label="Vence" value={profile?.license_expires_at || "—"} />
            </div>
          </div>

          {/* Forms */}
          <div className="space-y-4">
            <form onSubmit={submitProfile} className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6" data-testid="personal-form">
              <div className="flex items-center gap-2 mb-6">
                <IdCard size={16} className="text-[#C9A227]" />
                <h3 className="font-display font-bold text-sm uppercase tracking-wide">Datos de licencia</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="DNI"
                  value={profileForm.dni}
                  onChange={(e) => setProfileForm((f) => ({ ...f, dni: e.target.value }))}
                  required
                />
                <Field
                  label="Licencia Q"
                  value={profileForm.license_number}
                  onChange={(e) => setProfileForm((f) => ({ ...f, license_number: e.target.value }))}
                  required
                />
                <Field
                  label="Vencimiento de licencia"
                  type="date"
                  value={profileForm.license_expires_at}
                  onChange={(e) => setProfileForm((f) => ({ ...f, license_expires_at: e.target.value }))}
                  required
                />
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={savingProfile}
                  className="px-6 py-2.5 bg-[#C9A227] hover:bg-[#D4AF37] disabled:opacity-60 text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg"
                  data-testid="save-profile-btn"
                >
                  {savingProfile ? "Guardando…" : "Guardar cambios"}
                </button>
              </div>
            </form>

            {/* Vehículos */}
            <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6" data-testid="vehicles-section">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Car size={16} className="text-[#C9A227]" />
                  <h3 className="font-display font-bold text-sm uppercase tracking-wide">Mis vehículos</h3>
                </div>
                {profile && (
                  <button
                    type="button"
                    onClick={() => setShowVehicleForm((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs text-[#C9A227] uppercase tracking-wider font-display"
                    data-testid="toggle-new-vehicle"
                  >
                    <Plus size={14} /> Agregar
                  </button>
                )}
              </div>

              {!profile && (
                <p className="text-xs text-[#A0A0A5] font-body">Primero guarda tus datos de licencia para poder registrar vehículos.</p>
              )}

              {profile && vehicles.length === 0 && !showVehicleForm && (
                <p className="text-xs text-[#A0A0A5] font-body">Todavía no registraste ningún vehículo.</p>
              )}

              <div className="space-y-3">
                {vehicles.map((v) => (
                  <VehicleCard key={v.id} vehicle={v} onActivate={() => activateVehicle(v.id)} onUploadPhoto={(file) => uploadVehiclePhoto(v.id, file)} />
                ))}
              </div>

              {showVehicleForm && (
                <form onSubmit={submitVehicle} className="mt-4 pt-4 border-t border-[#2E2F33]">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Marca" value={vehicleForm.brand} onChange={(e) => setVehicleForm((f) => ({ ...f, brand: e.target.value }))} required />
                    <Field label="Modelo" value={vehicleForm.model} onChange={(e) => setVehicleForm((f) => ({ ...f, model: e.target.value }))} required />
                    <Field label="Año" type="number" value={vehicleForm.year} onChange={(e) => setVehicleForm((f) => ({ ...f, year: e.target.value }))} required />
                    <Field label="Color" value={vehicleForm.color} onChange={(e) => setVehicleForm((f) => ({ ...f, color: e.target.value }))} required />
                    <Field label="Placa" value={vehicleForm.plate} onChange={(e) => setVehicleForm((f) => ({ ...f, plate: e.target.value }))} required />
                    <Field
                      label="Capacidad (pasajeros)"
                      type="number"
                      value={vehicleForm.passenger_capacity}
                      onChange={(e) => setVehicleForm((f) => ({ ...f, passenger_capacity: e.target.value }))}
                      required
                    />
                    <Field
                      label="Capacidad (maletas)"
                      type="number"
                      value={vehicleForm.luggage_capacity}
                      onChange={(e) => setVehicleForm((f) => ({ ...f, luggage_capacity: e.target.value }))}
                    />
                    <div>
                      <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">Categoría</label>
                      <select
                        value={vehicleForm.category}
                        onChange={(e) => setVehicleForm((f) => ({ ...f, category: e.target.value }))}
                        className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none"
                      >
                        {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-6 flex items-center justify-end gap-3">
                    <button type="button" onClick={() => setShowVehicleForm(false)} className="px-5 py-2.5 border border-[#2E2F33] text-[#A0A0A5] hover:border-[#C9A227] uppercase tracking-wider font-display text-xs rounded-lg">
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={savingVehicle}
                      className="px-6 py-2.5 bg-[#C9A227] hover:bg-[#D4AF37] disabled:opacity-60 text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg"
                      data-testid="save-vehicle-btn"
                    >
                      {savingVehicle ? "Guardando…" : "Registrar vehículo"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </PanelShell>
  );
};

const VehicleCard = ({ vehicle, onActivate, onUploadPhoto }) => {
  const fileInput = useRef(null);
  return (
    <div className="bg-[#0D0E10] border border-[#2E2F33] rounded-lg p-4 flex items-center gap-4" data-testid={`vehicle-card-${vehicle.id}`}>
      <div className="w-14 h-14 rounded-lg bg-[#1C1D20] border border-[#2E2F33] overflow-hidden shrink-0 flex items-center justify-center">
        {vehicle.photo_url ? (
          <img src={resolveUploadUrl(vehicle.photo_url)} alt={vehicle.plate} className="w-full h-full object-cover" />
        ) : (
          <Car size={20} className="text-[#A0A0A5]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-body text-[#F5F5F5] truncate">
          {vehicle.brand} {vehicle.model} · {vehicle.year}
        </p>
        <p className="text-xs text-[#A0A0A5]">
          {vehicle.plate} · {CATEGORY_LABELS[vehicle.category] || vehicle.category} · {vehicle.passenger_capacity} pasajeros
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" onClick={() => fileInput.current?.click()} className="text-[10px] uppercase tracking-wider text-[#A0A0A5] hover:text-[#C9A227] font-display">
          Foto
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUploadPhoto(file);
            e.target.value = "";
          }}
        />
        {vehicle.is_active ? (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#C9A227] font-display" data-testid={`vehicle-active-${vehicle.id}`}>
            <CheckCircle2 size={12} /> En servicio
          </span>
        ) : (
          <button
            type="button"
            onClick={onActivate}
            className="text-[10px] uppercase tracking-wider text-[#A0A0A5] hover:text-[#C9A227] font-display border border-[#2E2F33] hover:border-[#C9A227] rounded-md px-2 py-1"
            data-testid={`activate-vehicle-${vehicle.id}`}
          >
            Activar
          </button>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, ...props }) => (
  <div>
    <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">{label}</label>
    <input {...props} className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none transition-colors" />
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-[#A0A0A5] uppercase tracking-wider font-display">{label}</span>
    <span className="text-[#F5F5F5] font-body truncate ml-2">{value}</span>
  </div>
);

export default DriverProfile;
